import { circleAiConversationsApi } from "@circle-react/api/circleAiConversationsApi";
import { CIRCLE_AI_CONVERSATIONS_QUERY_KEY } from "@circle-react/components/CircleAi/Home/RecentChatsDrawer/useRecentChatsList";
import { useCircleAiStore } from "@circle-react/components/CircleAi/useCircleAiStore";
import { circleAiProjectKey } from "@circle-react/lib/queries/circleAiProjects";
import { useCallback } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useConversationActionsToast } from "./useConversationActionsToast";

export interface ConversationProjectOption {
  name: string;
  uuid: string;
}

interface UseAddConversationToProjectOptions {
  buildMovedMessage: (projectName: string) => string;
  buildSuccessMessage: (projectName: string) => string;
  errorMessage: string;
  removeErrorMessage: string;
  removeSuccessMessage: string;
}

interface UpdateProjectVariables {
  errorMessage: string;
  projectId: string | null;
  successMessage: string;
  uuid: string;
}

export const useAddConversationToProject = ({
  buildMovedMessage,
  buildSuccessMessage,
  errorMessage,
  removeSuccessMessage,
  removeErrorMessage,
}: UseAddConversationToProjectOptions) => {
  const queryClient = useQueryClient();
  const { success, error } = useConversationActionsToast();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ uuid, projectId }: UpdateProjectVariables) =>
      circleAiConversationsApi.update({ id: uuid, projectId }),
    onSuccess: (_data, { uuid, projectId, successMessage }) => {
      void queryClient.invalidateQueries({
        queryKey: [CIRCLE_AI_CONVERSATIONS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: circleAiProjectKey.key(),
      });
      const store = useCircleAiStore.getState();
      if (uuid === store.activeConversation.id) {
        store.setActiveConversation({
          ...store.activeConversation,
          projectId,
        });
      }
      success(successMessage);
    },
    onError: (_err, { errorMessage: message }) => {
      error(message);
    },
  });

  const addToProject = useCallback(
    (uuid: string, project: ConversationProjectOption, isMove = false) =>
      mutate({
        uuid,
        projectId: project.uuid,
        successMessage: isMove
          ? buildMovedMessage(project.name)
          : buildSuccessMessage(project.name),
        errorMessage,
      }),
    [mutate, buildMovedMessage, buildSuccessMessage, errorMessage],
  );

  const removeFromProject = useCallback(
    (uuid: string) =>
      mutate({
        uuid,
        projectId: null,
        successMessage: removeSuccessMessage,
        errorMessage: removeErrorMessage,
      }),
    [mutate, removeSuccessMessage, removeErrorMessage],
  );

  return { addToProject, removeFromProject, isUpdatingProject: isPending };
};
